import type { DiscussionPeerActivityEvent } from '@jarvis/platform-file-review';
import { isSameUser } from '@jarvis/platform-file-review';
import type {
  PatientTrackingDiscussionChangedNotice,
  PatientTrackingDiscussionMessage,
  PatientTrackingDiscussionThreadDto
} from './patientTrackingDiscussionTypes';

function isActorCurrentUser(
  actorId: string | null | undefined,
  currentUserId: string,
  aliasSet: ReadonlySet<string>
): boolean {
  if (!actorId?.trim()) return false;
  if (isSameUser(actorId.trim(), currentUserId)) return true;
  for (const alias of aliasSet) {
    if (isSameUser(actorId.trim(), alias)) return true;
  }
  return false;
}

export function discoverPatientTrackingPeerActivity(
  previousThread: PatientTrackingDiscussionThreadDto | null,
  nextThread: PatientTrackingDiscussionThreadDto,
  notices: PatientTrackingDiscussionChangedNotice[],
  currentUserId: string,
  currentUserAliases: Iterable<string>,
  recordLabel: string
): DiscussionPeerActivityEvent | null {
  const aliasSet = new Set(currentUserAliases);
  const notice = notices.length > 0 ? notices[notices.length - 1]! : null;
  if (notice && isActorCurrentUser(notice.changedByUserId, currentUserId, aliasSet)) {
    return null;
  }

  if (!previousThread) {
    if (!notice) return null;
    return {
      changeType: notice.changeType,
      changedByDisplayName: notice.changedByDisplayName?.trim() || 'Someone',
      changedByUserId: notice.changedByUserId,
      changedAtUtc: notice.changedAtUtc,
      threadId: nextThread.threadId,
      mountId: nextThread.targetType,
      path: recordLabel,
      messageIds: nextThread.messages.map((m) => m.id)
    };
  }

  const prevMap = new Map(previousThread.messages.map((m) => [m.id, m]));
  const newMessages = nextThread.messages.filter(
    (m) => !prevMap.has(m.id) && !isPtMessageFromCurrentUser(m, currentUserId, aliasSet)
  );

  if (newMessages.length === 0 && !notice) return null;

  const first = newMessages[0];
  return {
    changeType: notice?.changeType ?? 'message-created',
    changedByDisplayName:
      notice?.changedByDisplayName?.trim() ||
      first?.submittedByDisplayName?.trim() ||
      first?.submittedBy?.trim() ||
      'Someone',
    changedByUserId: notice?.changedByUserId ?? first?.submittedByUserId ?? null,
    changedAtUtc: notice?.changedAtUtc ?? first?.submittedAt ?? new Date().toISOString(),
    threadId: nextThread.threadId,
    mountId: nextThread.targetType,
    path: recordLabel,
    messageIds: newMessages.map((m) => m.id)
  };
}

function isPtMessageFromCurrentUser(
  message: PatientTrackingDiscussionMessage,
  currentUserId: string,
  aliasSet: ReadonlySet<string>
): boolean {
  const keys = [message.submittedByUserId, message.submittedBy].filter(Boolean) as string[];
  for (const key of keys) {
    if (isSameUser(key, currentUserId)) return true;
    for (const alias of aliasSet) {
      if (isSameUser(key, alias)) return true;
    }
  }
  return false;
}
